import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { MessageSquareTextIcon, ArrowLeftIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { Tilt } from './Tilt';
export function ChatShowcase() {
  return <section className="relative overflow-hidden border-t border-slate-100 py-20 lg:py-28" data-id="element-15">
      <span aria-hidden="true" className="font-mono-brand pointer-events-none absolute -right-10 top-1/2 -translate-y-1/2 rotate-90 text-[10rem] font-bold tracking-tighter text-slate-900/[0.04] lg:text-[14rem]" data-id="element-16">
        RECALL
      </span>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-17">
        <div className="grid items-center gap-16 lg:grid-cols-2" data-id="element-18">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} data-id="element-19">
            <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-20">
              // borrower_profiles
            </p>
            <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl" data-id="element-21">
              Every borrower's story, in one place. Ask anything.
            </h2>
            <p className="mt-5 text-lg leading-8 text-slate-600" data-id="element-22">
              Recall files every call under the borrower's profile — a timeline
              of conversations, memory summary, open follow-ups, objections, and
              the exact quotes that matter. Prep for a call in two minutes, or
              chat with the history and get source-backed answers.
            </p>
            <ul className="mt-7 space-y-3" data-id="element-23">
              {['"What did this borrower say about income?"', '"What did I promise on the last call?"', '"What objections have they raised?"', '"What documents did I ask them for?"'].map(q => <li key={q} className="flex items-start gap-3" data-id="element-24">
                  <MessageSquareTextIcon className="mt-0.5 h-5 w-5 flex-none text-teal-600" aria-hidden="true" data-id="element-25" />
                  <span className="text-base font-medium text-slate-800" data-id="element-26">
                    {q}
                  </span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 32
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} aria-hidden="true" data-id="element-27">
            <Tilt data-id="element-28">
              <ProfileMockup data-id="element-29" />
            </Tilt>
          </motion.div>
        </div>
      </div>
    </section>;
}
function ProfileMockup() {
  return <div className="relative" data-id="element-30">
      <div className="glow-teal absolute inset-0 -z-10 rounded-xl" data-id="element-31" />
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100" data-id="element-32">
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2" data-id="element-33">
          <div className="flex items-center gap-1.5" data-id="element-34">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" data-id="element-35" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" data-id="element-36" />
            <span className="h-2.5 w-2.5 rounded-full bg-teal-400" data-id="element-37" />
          </div>
          <span className="text-[11px] font-medium text-slate-500" data-id="element-38">Recall</span>
          <span className="w-12" data-id="element-39" />
        </div>

        <div className="p-4" data-id="element-40">
          <p className="flex items-center gap-1 text-[10px] font-bold text-blue-900" data-id="element-41">
            <ArrowLeftIcon className="h-3 w-3" data-id="element-42" />
            Back to borrowers
          </p>

          {/* Header card */}
          <div className="mt-2.5 rounded-lg border border-slate-200 bg-white p-3.5" data-id="element-43">
            <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-44">
              <div className="flex items-center gap-2.5" data-id="element-45">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500 text-base font-extrabold text-white" data-id="element-46">
                  M
                </span>
                <p className="text-sm font-extrabold text-slate-900" data-id="element-47">
                  Michael Carter
                </p>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-48">
                <span className="rounded-md bg-blue-900 px-2.5 py-1 text-[10px] font-semibold text-white" data-id="element-49">
                  Prep for call
                </span>
                <span className="rounded-md border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-700" data-id="element-50">
                  Start call
                </span>
                <span className="rounded-md border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-700" data-id="element-51">
                  Chat
                </span>
                <span className="rounded-md border border-slate-200 p-1.5" data-id="element-52">
                  <PencilIcon className="h-2.5 w-2.5 text-slate-500" data-id="element-53" />
                </span>
                <span className="rounded-md border border-slate-200 p-1.5" data-id="element-54">
                  <Trash2Icon className="h-2.5 w-2.5 text-slate-400" data-id="element-55" />
                </span>
              </div>
            </div>
            <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3" data-id="element-56">
              <div className="rounded-md bg-slate-100 px-3 py-1.5" data-id="element-57">
                <p className="text-xs font-bold text-slate-900" data-id="element-58">2</p>
                <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500" data-id="element-59">
                  Calls
                </p>
              </div>
              <div className="rounded-md bg-slate-100 px-3 py-1.5" data-id="element-60">
                <p className="text-xs font-bold text-slate-900" data-id="element-61">6/10/2026</p>
                <p className="text-[9px] font-medium uppercase tracking-wide text-slate-500" data-id="element-62">
                  Last contact
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-5 gap-3" data-id="element-63">
            {/* Call timeline */}
            <div className="col-span-3 rounded-lg border border-slate-200 bg-white p-3" data-id="element-64">
              <p className="text-[11px] font-bold text-slate-900" data-id="element-65">
                Call timeline (2)
              </p>
              <div className="mt-2 space-y-2" data-id="element-66">
                <div className="rounded-md bg-slate-100 p-2.5" data-id="element-67">
                  <div className="flex items-center justify-between" data-id="element-68">
                    <p className="text-[10px] font-bold text-slate-800" data-id="element-69">
                      Call note
                    </p>
                    <p className="text-[8px] font-bold uppercase tracking-wide text-slate-500" data-id="element-70">
                      Completed
                    </p>
                  </div>
                  <p className="text-[9px] text-slate-500" data-id="element-71">
                    6/10/2026, 12:44 AM · 00:07
                  </p>
                  <p className="mt-1 text-[9px] leading-snug text-slate-600" data-id="element-72">
                    1. **Short Call Summary**: The borrower reached out to
                    discuss their mortgage options and clarify their financial
                    situation…
                  </p>
                </div>
                <div className="rounded-md bg-slate-100 p-2.5" data-id="element-73">
                  <div className="flex items-center justify-between" data-id="element-74">
                    <p className="text-[10px] font-bold text-slate-800" data-id="element-75">
                      Call note
                    </p>
                    <p className="text-[8px] font-bold uppercase tracking-wide text-slate-500" data-id="element-76">
                      Completed
                    </p>
                  </div>
                  <p className="text-[9px] text-slate-500" data-id="element-77">
                    6/9/2026, 10:20 PM · 00:59
                  </p>
                  <p className="mt-1 text-[9px] leading-snug text-slate-600" data-id="element-78">
                    1. **Short Call Summary**: Michael expressed interest in a
                    mortgage for his first home, acknowledging current high
                    rates. Flexible timeline, shopping around…
                  </p>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="col-span-2 space-y-3" data-id="element-79">
              <div className="rounded-lg border border-slate-200 bg-white p-3" data-id="element-80">
                <p className="text-[11px] font-bold text-slate-900" data-id="element-81">
                  Open follow-ups
                </p>
                <p className="mt-1 text-[9px] text-slate-500" data-id="element-82">
                  Confirm bonus income eligibility
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-3" data-id="element-83">
                <p className="text-[11px] font-bold text-slate-900" data-id="element-84">
                  Recent quotes
                </p>
                <div className="mt-2 space-y-2" data-id="element-85">
                  <p className="border-l-2 border-teal-500 pl-2 text-[9px] italic leading-snug text-slate-600" data-id="element-86">
                    borrower: "I'm looking for my first home."
                  </p>
                  <p className="border-l-2 border-teal-500 pl-2 text-[9px] italic leading-snug text-slate-600" data-id="element-87">
                    borrower: "I know rates are kinda high right now, so I was
                    just, like, shopping around, seeing what they're at right
                    now."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-5 -left-5 -z-10 h-full w-full rounded-xl bg-teal-50" data-id="element-88" />
    </div>;
}